import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { echo } from '../lib/echo';

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  body?: string | null;
  data?: Record<string, any> | null;
  read_at: string | null;
  created_at: string;
}

interface NotificationsResponse {
  data: AppNotification[];
  unread_count: number;
}

const QK = ['notifications'];

export function useNotifications() {
  const queryClient = useQueryClient();

  const query = useQuery<NotificationsResponse>({
    queryKey: QK,
    queryFn: async () => {
      const { data } = await api.get('/notifications');
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // ── Real-time: listen for new notifications via WebSocket ──────────────
  useEffect(() => {
    const ch = echo.channel('notifications');

    ch.listen('.notification.created', (event: { notification: AppNotification }) => {
      queryClient.setQueryData<NotificationsResponse>(QK, prev => {
        if (!prev) return { data: [event.notification], unread_count: 1 };
        return {
          data: [event.notification, ...prev.data].slice(0, 50),
          unread_count: prev.unread_count + 1,
        };
      });

      // Play a subtle sound
      playNotificationSound();
    });

    return () => { echo.leave('notifications'); };
  }, [queryClient]);

  // ── Mutations ──────────────────────────────────────────────────────────
  const markRead = useMutation({
    mutationFn: (id: number) => api.patch(`/notifications/${id}/read`),
    onSuccess: (_, id) => {
      queryClient.setQueryData<NotificationsResponse>(QK, prev => {
        if (!prev) return prev;
        const data = prev.data.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n);
        const unread_count = Math.max(0, prev.unread_count - 1);
        return { data, unread_count };
      });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.patch('/notifications/read-all'),
    onSuccess: () => {
      queryClient.setQueryData<NotificationsResponse>(QK, prev => {
        if (!prev) return prev;
        const now = new Date().toISOString();
        return {
          data: prev.data.map(n => ({ ...n, read_at: n.read_at ?? now })),
          unread_count: 0,
        };
      });
    },
  });

  const clearRead = useMutation({
    mutationFn: () => api.delete('/notifications/read'),
    onSuccess: () => {
      queryClient.setQueryData<NotificationsResponse>(QK, prev => {
        if (!prev) return prev;
        return { data: prev.data.filter(n => !n.read_at), unread_count: prev.unread_count };
      });
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: number) => api.delete(`/notifications/${id}`),
    onSuccess: (_, id) => {
      queryClient.setQueryData<NotificationsResponse>(QK, prev => {
        if (!prev) return prev;
        const removed = prev.data.find(n => n.id === id);
        return {
          data: prev.data.filter(n => n.id !== id),
          unread_count: removed && !removed.read_at ? Math.max(0, prev.unread_count - 1) : prev.unread_count,
        };
      });
    },
  });

  return {
    notifications: query.data?.data ?? [],
    unreadCount:   query.data?.unread_count ?? 0,
    isLoading:     query.isLoading,
    markRead:      markRead.mutate,
    markAllRead:   markAllRead.mutate,
    clearRead:     clearRead.mutate,
    dismiss:       dismiss.mutate,
  };
}

// ── Subtle notification sound using Web Audio API ──────────────────────────

function playNotificationSound() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    // AudioContext not available (tests, SSR, etc.)
  }
}
